/**
 * AnnotationRegistry — single source of truth for per-UserAnnotation-type
 * behavior. Mirrors `block-view.tsx`'s `contentRegistry` exactly:
 *
 *   contentRegistry  : block.type   → ContentComponent
 *   annotationRegistry : annotation.type → AnnotationRegistryEntry
 *
 * Adding a new annotation type means adding one entry here. Every other
 * site (the presentational `AnnotationView`, the optimistic-row builder
 * in `user-annotation-store.ts`, the position resolver) reads from the
 * registry instead of repeating its own switch over `annotation.type`.
 *
 * Why a `Map` (not a `Record`)? The block side already uses
 * `new Map<string, React.ComponentType<...>>()`; matching the shape
 * keeps the patterns visually identical for anyone reading both
 * registries. `Map.get(...)` returns `undefined` for unknown keys,
 * which `resolveAnnotationComponent` translates into the `Unsupported`
 * fallback below — same shape as `block-view.tsx`'s `UnsupportedBlockContent`.
 */
import { memo } from "react";
import { CommentAnnotationView } from "./views/comment-annotation-view";
import { DrawingAnnotationView } from "./views/drawing-annotation-view";
import { StickerAnnotationView } from "./views/sticker-annotation-view";
import { StampAnnotationView } from "./views/stamp-annotation-view";
import { TextHighlightAnnotationView } from "./views/text-highlight-annotation-view";
import { STAMP_BASE_HEIGHT_NORM } from "./views/stamp-constants";
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
const annotationRegistry = new Map();
annotationRegistry.set("sticker", {
    kind: "mark",
    Component: StickerAnnotationView,
});
annotationRegistry.set("stamp", {
    kind: "mark",
    Component: StampAnnotationView,
});
annotationRegistry.set("drawing", {
    kind: "read-only",
    Component: DrawingAnnotationView,
});
annotationRegistry.set("text_highlight", {
    kind: "read-only",
    Component: TextHighlightAnnotationView,
});
annotationRegistry.set("comment", {
    kind: "read-only",
    Component: CommentAnnotationView,
});
/**
 * Mirrors `resolveContentComponent(block)` in `block-view.tsx`. Returns
 * `undefined` for unknown types so the caller can fall back to
 * `UnsupportedAnnotationView` (parallel to `UnsupportedBlockContent`).
 */
export function resolveAnnotationEntry(annotation) {
    return annotationRegistry.get(annotation.type);
}
/**
 * Read whether a given annotation type expects the gesture stack.
 * Driven from the same registry as the Component lookup — one place
 * to flip a type's interactivity instead of two parallel switches.
 */
export function isInteractiveType(type) {
    return annotationRegistry.get(type)?.kind === "mark";
}
// ---------------------------------------------------------------------------
// Unsupported fallback
//
// Mirrors `block-view.tsx`'s `UnsupportedBlockContent`. Renders nothing
// today — a yet-unknown annotation type from a newer client should be
// silently ignored by an older renderer rather than crashing the
// stream. Logging is deferred until we have an out-of-band channel
// for surfacing forward-compat misses.
// ---------------------------------------------------------------------------
export const UnsupportedAnnotationView = memo(function UnsupportedAnnotationView(_props) {
    return null;
});
// ---------------------------------------------------------------------------
// Position resolver
//
// One place to derive `(x, y, rotation, scale)` from an annotation's
// `content` / `anchor`. Mark types (sticker / stamp) are the only
// callers that actually use the result today — the read-only types
// position themselves from their own content / anchor and ignore the
// position prop. Keeping the resolver here (instead of inside each
// registry entry) means one switch over `annotation.type` rather
// than per-entry data plus a switch.
// ---------------------------------------------------------------------------
const DEFAULT_POSITION = {
    x: 0.5,
    y: 0.5,
    rotation: 0,
    scale: 1,
};
export function defaultPositionFor(annotation) {
    // Phase 2 unified spatial position into the top-level `anchor`
    // (`panel_point` selector). Stickers and stamps both read their
    // `(x, y)` from the anchor; size + rotation come from
    // `content.transform`.
    const point = annotation.anchor?.type === "panel_point" ? annotation.anchor : undefined;
    if (annotation.type === "sticker") {
        const t = annotation.content.transform;
        return {
            x: point?.x ?? DEFAULT_POSITION.x,
            y: point?.y ?? DEFAULT_POSITION.y,
            rotation: t?.rotation ?? 0,
            // `width`/`height` in transform are normalized 0–1 sizes; map
            // to a uniform scale where 1.0 (the schema's "natural") maps to
            // the View's intrinsic size.
            scale: t?.width ? t.width / 0.1 : 1,
        };
    }
    if (annotation.type === "stamp") {
        const t = annotation.content.transform;
        // Stamps carry their position on the row's `anchor` and only
        // size/rotation in `content.transform`. Rendered scale =
        // persisted `height / STAMP_BASE_HEIGHT_NORM` so a stamp saved
        // at the base height renders at scale 1.0. Reading `(x, y)` from
        // anchor means stream cards paint stamps at the same place the
        // user dropped them in the modal panel — no out-of-band side
        // channel.
        return {
            x: point?.x ?? 0.5,
            y: point?.y ?? 0.5,
            rotation: t?.rotation ?? 0,
            scale: t?.height ? t.height / STAMP_BASE_HEIGHT_NORM : 1,
        };
    }
    return DEFAULT_POSITION;
}
